import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import { AuthProvider } from "@/context/AuthContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import AppHeader from "@/components/AppHeader";
import AppFooter from "@/components/AppFooter";
import WhatsAppBubble from "@/components/WhatsAppBubble";

// Eager: homepage (critical path)
import Index from "./pages/Index";

// Lazy: all other pages
const Cauta = lazy(() => import("./pages/Cauta"));
const Pachete = lazy(() => import("./pages/Pachete"));
const Request = lazy(() => import("./pages/Request"));
const Status = lazy(() => import("./pages/Status"));
const Auth = lazy(() => import("./pages/Auth"));
const Cont = lazy(() => import("./pages/Cont"));
const Admin = lazy(() => import("./pages/Admin"));
const Partener = lazy(() => import("./pages/Partener"));
const NotFound = lazy(() => import("./pages/NotFound"));
const CumFunctioneaza = lazy(() => import("./pages/CumFunctioneaza"));
const Orase = lazy(() => import("./pages/Orase"));
const DespreNoi = lazy(() => import("./pages/DespreNoi"));
const SurprizeOras = lazy(() => import("./pages/SurprizeOras"));
const OcazieTemplate = lazy(() => import("./pages/OcazieTemplate"));
const Contact = lazy(() => import("./pages/Contact"));
const DevinoPartener = lazy(() => import("./pages/DevinoPartener"));
const InregistrarePartener = lazy(() => import("./pages/InregistrarePartener"));

const queryClient = new QueryClient();

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <Toaster />
      <Sonner />
      <BrowserRouter>
        <AuthProvider>
          <AppHeader />
          <Suspense fallback={<div className="min-h-screen" />}>
            <Routes>
              <Route path="/" element={<Index />} />
              <Route path="/comanda-surpriza" element={<Cauta />} />
              <Route path="/cauta" element={<Navigate to="/comanda-surpriza" replace />} />
              <Route path="/cum-functioneaza" element={<CumFunctioneaza />} />
              <Route path="/orase" element={<Orase />} />
              <Route path="/despre-noi" element={<DespreNoi />} />
              <Route path="/surprize-la-domiciliu/:citySlug" element={<SurprizeOras />} />
              <Route path="/ocazii/:occasionSlug" element={<OcazieTemplate />} />
              <Route path="/pachete" element={<Pachete />} />
              <Route path="/comanda" element={<Request />} />
              <Route path="/request" element={<Navigate to="/comanda" replace />} />
              <Route
                path="/status/:orderId"
                element={
                  <ProtectedRoute>
                    <Status />
                  </ProtectedRoute>
                }
              />
              <Route path="/autentificare" element={<Auth />} />
              <Route path="/auth" element={<Navigate to="/autentificare" replace />} />
              <Route
                path="/cont"
                element={
                  <ProtectedRoute>
                    <Cont />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/admin"
                element={
                  <ProtectedRoute requiredRole="admin">
                    <Admin />
                  </ProtectedRoute>
                }
              />
              <Route
                path="/partener"
                element={
                  <ProtectedRoute requiredRole="partner">
                    <Partener />
                  </ProtectedRoute>
                }
              />
              <Route path="/contact" element={<Contact />} />
              <Route path="/devino-partener" element={<DevinoPartener />} />
              <Route path="/inregistrare-partener" element={<InregistrarePartener />} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          <AppFooter />
          <WhatsAppBubble />
        </AuthProvider>
      </BrowserRouter>
    </TooltipProvider>
  </QueryClientProvider>
);

export default App;
